import * as Types from '../types';

export const isInternal = (user: Types.LoginUserProfile) => {
  const username = user.username || '';
  return username.endsWith('@conviva.com') || username.endsWith('@conviva.ai');
};

// RFC 5321/5322 compliant email validation (dot-atom form). Keep in sync with the server-side
// `regEmail` in instant-filter-server/src/user-management/service/utils/constants.ts.
// - Local-part: full atext set (A-Za-z0-9 and !#$%&'*+/=?^_`{|}~-), dot-separated, no leading/trailing/consecutive dots
// - Domain:     dot-separated labels of 1-63 chars, no leading/trailing hyphen; allows punycode TLDs like xn--abc
// Quoted local-parts and IP-literal domains are intentionally not supported (Okta does not accept them).
const ATEXT = "A-Za-z0-9!#$%&'*+/=?^_`{|}~-";
const LOCAL_PART = `[${ATEXT}]+(?:\\.[${ATEXT}]+)*`;
const DOMAIN_LABEL = `[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?`;
const DOMAIN = `(?:${DOMAIN_LABEL}\\.)+${DOMAIN_LABEL}`;
export const regEmail = new RegExp(`^${LOCAL_PART}@${DOMAIN}$`);

// Curly / smart quotes look like ' or " but are not valid in an RFC address. We detect them
// separately so we can surface a specific, actionable error rather than the generic
// "invalid email" message -- this is a common copy-paste failure from Outlook / Word / Mail.
const SMART_QUOTE_RE = /[\u2018\u2019\u201A\u201B\u2032\u201C\u201D\u201E\u201F\u2033]/;

/**
 * Validate per RFC 5321/5322 with length limits.
 * Returns null when valid, otherwise a human-readable message suitable for surfacing in the form.
 */
export function getEmailValidationError(input: unknown): string | null {
  const email = String(input ?? '').trim();
  if (email.length === 0) return 'invalid email';
  if (SMART_QUOTE_RE.test(email)) {
    return `invalid email: ${email} contains a curly/smart quote (’ or ”) — please retype using a straight apostrophe (') or quote (")`;
  }
  if (email.length > 254) return `invalid email: ${email}`;
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return `invalid email: ${email}`;
  if (email.slice(0, at).length > 64) return `invalid email: ${email}`;
  if (email.slice(at + 1).length > 255) return `invalid email: ${email}`;
  if (!regEmail.test(email)) return `invalid email: ${email}`;
  return null;
}

export function isValidEmail(input: unknown): boolean {
  return getEmailValidationError(input) === null;
}

export const MAX_USER_COUNT_TO_OPERATE = 50;
export const MAX_ACCOUNTS_COUNT_TO_OPERATE = 50;

/**
 * Separators for invite email lists — keep in sync with `EMAIL_LIST_SPLIT` and the invite form label.
 * (Not full `\s`: avoids splitting on other Unicode whitespace we do not advertise.)
 */
export const INVITE_EMAIL_TOKEN_SEPARATORS = [',', ';', ' ', '\n', '\r', '\t'] as const;

/** Same characters as `INVITE_EMAIL_TOKEN_SEPARATORS`. */
const EMAIL_LIST_SPLIT = /[,; \n\r\t]+/;

export function normalizeInviteEmailTags(value: unknown): string[] {
  if (value == null) {
    return [];
  }
  const entries = Array.isArray(value) ? value : [String(value)];
  const emails: string[] = [];
  for (const entry of entries) {
    if (entry == null || entry === '') {
      continue;
    }
    const str = String(entry).trim();
    if (!str) {
      continue;
    }
    const parts = str
      .split(EMAIL_LIST_SPLIT)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    emails.push(...parts);
  }
  return emails;
}

export function splitEmails(str: string) {
  if (!str) {
    return [];
  }
  const emails = str
    .split(EMAIL_LIST_SPLIT)
    .map((email) => email.trim())
    .filter((it) => it.length > 0);
  for (const email of emails) {
    const error = getEmailValidationError(email);
    if (error) {
      throw new Error(error);
    }
  }
  return emails;
}

export enum State {
  requestInviteSuccess = 'Send request success',
  requestInviteFail = 'Send request fail',
  requestRevokeSuccess = 'Send request success',
  requestRevokeFail = 'Send request fail',
  requestEditFail = 'Edit user fail',
  requestBulkEditSuccess = 'Permissions updated',
  requestBulkEditFail = 'Failed to update permissions',
  emailCanNotBeEmpty = 'Can not be empty',
}

export function editUserSuccessMsg(email: string) {
  return `Edit user ${email ?? ''} successfully`;
}
